import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';

interface StorageProvider {
  setItem(key: string, value: string): void;
  getItem(key: string): string | null;
  removeItem(key: string): void;
}

class LocalStorageProvider implements StorageProvider {
  setItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  getItem(key: string): string | null {
    return localStorage.getItem(key);
  }

  removeItem(key: string): void {
    localStorage.removeItem(key);
  }
}

class SessionStorageProvider implements StorageProvider {
  setItem(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  getItem(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }
}

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly TOKEN_KEY = environment.storage.tokenKey;
  private readonly REMEMBER_KEY = environment.storage.rememberKey;
  private readonly USER_KEY = environment.storage.userKey;
  private storageProvider: StorageProvider;

  constructor() {
    const rememberMe = localStorage.getItem(this.REMEMBER_KEY) === 'true';
    this.storageProvider = rememberMe
      ? new LocalStorageProvider()
      : new SessionStorageProvider();
  }

  setRememberMe(remember: boolean): void {
    localStorage.setItem(this.REMEMBER_KEY, String(remember));
    this.storageProvider = remember
      ? new LocalStorageProvider()
      : new SessionStorageProvider();

    if (!remember) {
      const currentToken = this.getToken();
      const currentUser = this.getUser();
      if (currentToken) {
        localStorage.removeItem(this.TOKEN_KEY);
        sessionStorage.setItem(this.TOKEN_KEY, currentToken);
      }
      if (currentUser) {
        localStorage.removeItem(this.USER_KEY);
        sessionStorage.setItem(this.USER_KEY, currentUser);
      }
    }
  }

  setToken(token: string): void {
    this.storageProvider.setItem(this.TOKEN_KEY, token);
  }

  getToken(): string | null {
    return this.storageProvider.getItem(this.TOKEN_KEY);
  }

  setUser(user: string): void {
    this.storageProvider.setItem(this.USER_KEY, user);
  }

  getUser(): string | null {
    return this.storageProvider.getItem(this.USER_KEY);
  }

  clear(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }
}
